/**
 * Recent sync runs (Phase 65 / BLU-594 PR3b). A quiet, collapsible history of
 * the last runs — status pill + relative time + a link into each job page.
 * Renders nothing when there are no runs (no empty noise on a first-time org).
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronRight, History } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { JobStatusPill } from './job/JobStatusPill';
import { useProductSyncJobs } from '../hooks/useProductSyncJob';
import { useDateFnsLocale } from '@/lib/format-dates';

const RECENT_LIMIT = 6;

export function RecentSyncRuns() {
  const { t } = useTranslation('productSync');
  const locale = useDateFnsLocale();
  const { data } = useProductSyncJobs();
  const runs = (data ?? []).slice(0, RECENT_LIMIT);

  if (runs.length === 0) return null;

  return (
    <Collapsible.Root className="mt-4">
      <Collapsible.Trigger asChild>
        <button
          type="button"
          className="group flex items-center gap-2 rounded-md px-1 py-1 text-xs font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground motion-reduce:transition-none"
        >
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90 motion-reduce:transition-none" aria-hidden="true" />
          <History className="h-3.5 w-3.5" aria-hidden="true" />
          {t('job.recent.title', { count: runs.length })}
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ul className="mt-2 divide-y rounded-lg border">
          {runs.map((run) => (
            <li key={run.id}>
              <Link
                to={`/product-sync/jobs/${run.id}`}
                className="flex items-center gap-3 px-3 py-2 text-xs transition-colors duration-200 hover:bg-muted/40 motion-reduce:transition-none"
              >
                <JobStatusPill status={run.status} />
                <span className="font-mono text-muted-foreground">{run.id.slice(0, 8)}</span>
                <span className="ml-auto tabular-nums text-muted-foreground/70">
                  {formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
